/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, TextField, Button, Dialog, DialogTitle, Typography, DialogContent, useMediaQuery, Box, FormControl, InputLabel, Select, SelectChangeEvent, MenuItem, Paper, ButtonGroup, Tabs, Tab, DialogActions, Backdrop, CircularProgress, List, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, FormControlLabel, Switch, IconButton } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, useRef, useContext, useMemo, ChangeEvent } from "react";

import { useTranslation } from 'react-i18next';
import { ArrowBack, ArrowForward, AssignmentInd, CallSplit, CheckCircle, Clear, NoteAdd, Save, SaveAlt, ThumbDown, Update, Warning, Error, Download} from '@mui/icons-material';
import FileViewer from '../../voucherSearch/components/FileViewer';
import { toast } from 'react-toastify';
import DialogVoucherRegistrationLines from './DialogVoucherRegistrationLines';
import { IFileGuiModel, ISourceDocumentGuiModel, ISourceDocumentGuiModifyModel, ISourceDocumentLineGuiModel, ISourceDocumentMarkCompletedGuiModel } from '../../../model/ISourceDocumentGuiModel';
import { useParams } from 'react-router-dom';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DateValidationError } from '@mui/x-date-pickers';
import SourceDocumentBusinessLogic from '../../../businessLogic/SourceDocumentBusinessLogic';

interface IDialogProps {
    open: boolean,
    closeDialog: (reason?: "backdropClick" | "escapeKeyDown") => void,
    createNew?: boolean,
    initialDocument: ISourceDocumentGuiModel,
    initialDocumentList: ISourceDocumentGuiModel[],
    initialDocumentListIndex: number,
    banking?: boolean
}

const DialogVoucherRegistration = (props: IDialogProps): JSX.Element => {

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const { organizationId } = useParams();

    const [loader, setLoader] = useState(false);

    const [createNew, setCreateNew] = useState(props.createNew ? true : false);

    const [document, setDocument] = useState<ISourceDocumentGuiModel>(props.initialDocument);
    const [document2, setDocument2] = useState<ISourceDocumentGuiModel>(props.initialDocument);
    const [documentIndex, setDocumentIndex] = useState(props.initialDocumentListIndex);
    const [documentIndex2, setDocumentIndex2] = useState(props.initialDocumentListIndex);

    useEffect(() => {

        setDocument(document2);
        setDocumentIndex(documentIndex2);

    }, [document2, documentIndex2])

    const logicGetFile = async (org: string, fileId: number, sourceId: string) => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsFile(
            org,
            fileId,
            sourceId
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = window.document.createElement("a");
            link.download = "voucher" + sourceId + "-" + fileId;
            link.href = blobUrl;
            link.click();
        }
    }

    const logicGetFilePDF = async (org: string, fileId: number, sourceId: string) => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsFileDownloadPDF(
            org,
            fileId,
            sourceId
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = window.document.createElement("a");
            link.download = "voucher" + sourceId + "-" + fileId + ".pdf";
            link.href = blobUrl;
            link.click();
        }
    }

    const [ignoreWarnings, setIgnoreWarnings] = useState(false);
    const [markcompletedResponseList, setMarkCompletedResponseList] = useState<ISourceDocumentMarkCompletedGuiModel[]>([]);

    const handleUpdateDocument = async (type: "previous" | "next" | "update", newDocumentId?: number) => {
        const logic = new SourceDocumentBusinessLogic();

        let documentId: number;

        if (newDocumentId) {
            documentId = newDocumentId;
        } else {
            documentId = document.id;
        }

        if (organizationId) {
            if (type === "previous" && !newDocumentId) {
                if (documentIndex === 0) { // Using documentIndex state
                    props.closeDialog();
                    return; // Avoid calling API
                } else {
                    const newIndex = documentIndex - 1;
                    setDocumentIndex2(newIndex); // Update the state
                    documentId = props.initialDocumentList[newIndex].id;
                }
            } else if (type === "next" && !newDocumentId) {
                if (documentIndex === props.initialDocumentList.length - 1) { // Using documentIndex state
                    props.closeDialog();
                    return; // Avoid calling API
                } else {
                    const newIndex = documentIndex + 1;
                    setDocumentIndex2(newIndex); // Update the state
                    documentId = props.initialDocumentList[newIndex].id;
                }
            }

            setLoader(true);
            const result = await logic.getSourceDocument(organizationId, documentId);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("voucherRegistration.errorDocumentUpdate"));
                props.closeDialog();
            } else {

                setDocument2(result.value);

                console.log("Testing doc update");
                if (result.value && result.value.files) {
                    let index = result.value.files.findIndex(f => f.isPrimary === true);
                    if (index !== -1) {
                        setFileViewed(result.value.files[index]);
                    } else {
                        setFileViewed(result.value.files[0]);
                    }
                } else {
                    setFileViewed(null);
                }
            }

        } else {
            toast(t("basic.errorWithOrganization"));
            props.closeDialog();
        }
    };

    const handleUploadFileChange = (e: ChangeEvent<HTMLInputElement>,) => {

        const file: File | null = e.target.files ? e.target.files[0] : null;

        if (file && (file['type'].split('/')[0] === 'image' || file['type'] === "application/pdf")) {
            postSourceDocumentFile(file);
        }
        else {
            toast(t("basic.invalidFile"));
        }
        e.target.value = "";
    }

    const postSourceDocumentFile = async (file: File) => {
        const logic = new SourceDocumentBusinessLogic();

        if (organizationId) {
            setLoader(true);
            const result = await logic.postSourceDocumentFile(organizationId, document.id, file);
            setLoader(false);

            if (result.hasErrors) {
                toast(t("basic.errorFile"));
            } else {
                toast(t("basic.fileUploaded"));
                setCreateNew(false);
                handleUpdateDocument("update");
            }

        } else {
            toast(t("basic.errorWithOrganization"));
            props.closeDialog();
        }
    }

    const [fileViewed, setFileViewed] = useState<IFileGuiModel | null>(() => {
        if (props.initialDocument && props.initialDocument.files) {
            let index = props.initialDocument.files.findIndex(f => f.isPrimary === true);
            if (index !== -1) {
                return props.initialDocument.files[index];
            } else {
                return props.initialDocument.files[0];
            }
        } else {
            return null;
        }
    });

    const [rejectDialog, setRejectDialog] = useState(false);
    const handleOpenRejectDialog = () => {
        setRejectDialog(true);
    }
    const handleCloseRejectDialog = () => {
        setRejectDialog(false);
    }
    const [saveDialog, setSaveDialog] = useState(false);
    const handleOpenSaveDialog = () => {
        setSaveDialog(true);
    }
    const handleCloseSaveDialog = () => {
        setSaveDialog(false);
        setMarkCompletedResponseList([]);
    }

    const [processDialog, setProcessDialog] = useState(false);
    const handleOpenProcessDialog = () => {
        setProcessDialog(true);
    }
    const handleCloseProcessDialog = () => {
        setProcessDialog(false);
    }

    const handleDocumentReject = async () => {
        
        const logic = new SourceDocumentBusinessLogic();

        if (organizationId) {

            if (validateData()) {

                const modifiedDocumentLines: ISourceDocumentGuiModifyModel[] = document.lines ? document.lines.map((line, index) => {
                    return {
                        invoiceNo: line.invoiceNo ? line.invoiceNo : null,
                        debitAccount: line.debitAccount && line.debitAccount.accountId ? line.debitAccount.accountId : null,
                        debitAccountType: line.debitAccount && line.debitAccount.accountType ? line.debitAccount.accountType : null,
                        debitVAT: line.debitVat && line.debitVat.vatCodeId ? line.debitVat.vatCodeId : null,
                        creditAccount: line.creditAccount && line.creditAccount.accountId ? line.creditAccount.accountId : null,
                        creditAccountType: line.creditAccount && line.creditAccount.accountType ? line.creditAccount.accountType : null,
                        creditVAT: line.creditVat && line.creditVat.vatCodeId ? line.creditVat.vatCodeId : null,
                        amount: line.amount || line.amount === 0 ? line.amount : null,
                        voucherDate: line.voucherDate,
                        dueDate: line.dueDate,
                        valueDate: line.valueDate,
                        projectId: line.project ? line.project.projectId : null,
                        departmentId: line.department ? line.department.departmentId : null,
                        text: line.text ? line.text : null,
                        crossReference: line.crossReference ? line.crossReference : null,
                        rowNo: index,
                        cid: line.cid ? line.cid : null,
                        currency: line.currency ? line.currency : null,
                        currencyVismaId: line.currencyVismaId ? line.currencyVismaId : null,
                        currencyRate: line.currencyRate || line.currencyRate === 0 ? line.currencyRate : null,
                        currencyAmount: line.currencyAmount || line.currencyAmount === 0 ? line.currencyAmount : null,
                        currencyDate: line.currencyDate,
                    }
                }) : [];

                setLoader(true);
                const result1 = await logic.putSourceDocumentModify(organizationId, document.id, document.comments ? document.comments : "", modifiedDocumentLines);
                setLoader(false);
                if (result1.hasErrors) {
                    toast(t("voucherRegistration.errorSaveDraft") + result1.errors.join(", "));
                }
                else {
                    toast(t("voucherRegistration.documentDraftSaved"));
                    setLoader(true);
                    const result2 = await logic.postSourceDocumentReject(organizationId, [document.id]);
                    setLoader(false);
                    if (result2.hasErrors) {
                        toast(t("voucherRegistration.errorReject"))
                    } else {
                        toast(t("voucherRegistration.documentRejected"));
                        handleCloseRejectDialog();
                        handleUpdateDocument("next");
                    }
                }
            }

        } else {
            toast(t("basic.errorWithOrganization"));
        }
        
    }

    const handleNewDocumentSave = async (type: "draft" | "permanent", modifiedDocumentLines: ISourceDocumentGuiModifyModel[]) => {

        if (organizationId) {
            const logic = new SourceDocumentBusinessLogic();
            setLoader(true);
            const result = await logic.postSourceDocument(organizationId, "Miscellaneous", document.comments ? document.comments : "", quill, modifiedDocumentLines);
            setLoader(false);

            if (result.hasErrors) {
                if (type === "draft") {
                    toast(t("voucherRegistration.errorSaveDraft") + result.errors.join(", "));
                } else {
                    toast(t("voucherRegistration.errorSave") + result.errors.join(", "))
                }
            } else {
                if (type === "draft") {
                    toast(t("voucherRegistration.documentDraftSaved"));
                    props.closeDialog();
                } else {
                    const newId = result.value?.sourceDocumentId as number; //Not undefined. See SourceDocumetBusinessLogic
                    handleUpdateDocument("update", newId);
                    setCreateNew(false);

                    setLoader(true);
                    const result2 = await logic.postSourceDocumentMarkCompleted(organizationId, newId, false);
                    setLoader(false);

                    if (result2.hasErrors) {
                        setMarkCompletedResponseList([]);
                        toast(t("voucherRegistration.errorSave") + result2.errors.join(", "))
                    } else {
                        if (result2.value.length > 0) { //Any errors or warnings? If so show them to the user
                            handleOpenSaveDialog();
                            setMarkCompletedResponseList(result2.value);
                        } else {
                            toast(t("voucherRegistration.documentSaved"));
                            handleCloseSaveDialog();
                            props.closeDialog();
                        }
                    }
                }
            }

        } else {
            toast(t("basic.errorWithOrganization"));
            props.closeDialog();
        }

    }

    const handleDocumentSave = async (type: "draft" | "permanent") => {

        const logic = new SourceDocumentBusinessLogic();

        if (organizationId) {
            if (validateData()) {

                const modifiedDocumentLines: ISourceDocumentGuiModifyModel[] = document.lines ? document.lines.map((line, index) => {
                    return {
                        invoiceNo: line.invoiceNo ? line.invoiceNo : null,
                        debitAccount: line.debitAccount && line.debitAccount.accountId ? line.debitAccount.accountId : null,
                        debitAccountType: line.debitAccount && line.debitAccount.accountType ? line.debitAccount.accountType : null,
                        debitVAT: line.debitVat && line.debitVat.vatCodeId ? line.debitVat.vatCodeId : null,
                        creditAccount: line.creditAccount && line.creditAccount.accountId ? line.creditAccount.accountId : null,
                        creditAccountType: line.creditAccount && line.creditAccount.accountType ? line.creditAccount.accountType : null,
                        creditVAT: line.creditVat && line.creditVat.vatCodeId ? line.creditVat.vatCodeId : null,
                        amount: line.amount || line.amount === 0 ? line.amount : null,
                        voucherDate: line.voucherDate,
                        dueDate: line.dueDate,
                        valueDate: line.valueDate,
                        projectId: line.project ? line.project.projectId : null,
                        departmentId: line.department ? line.department.departmentId : null,
                        text: line.text ? line.text : null,
                        crossReference: line.crossReference ? line.crossReference : null,
                        rowNo: index,
                        cid: line.cid ? line.cid : null,
                        currency: line.currency ? line.currency : null,
                        currencyVismaId: line.currencyVismaId ? line.currencyVismaId : null,
                        currencyRate: line.currencyRate || line.currencyRate === 0 ? line.currencyRate : null,
                        currencyAmount: line.currencyAmount || line.currencyAmount === 0 ? line.currencyAmount : null,
                        currencyDate: line.currencyDate,
                    }
                }) : [];

                if (createNew) {
                    handleNewDocumentSave(type, modifiedDocumentLines);
                } else {
                    setLoader(true);
                    const result = await logic.putSourceDocumentModify(organizationId, document.id, document.comments ? document.comments : "", modifiedDocumentLines);
                    setLoader(false);
                    if (result.hasErrors) {
                        if (type === "draft") {
                            toast(t("voucherRegistration.errorSaveDraft") + result.errors.join(", "));
                        } else {
                            toast(t("voucherRegistration.errorSave") + result.errors.join(", "))
                        }
                    } else {
                        if (type === "draft") {
                            toast(t("voucherRegistration.documentDraftSaved"));
                            handleUpdateDocument("next");
                        } else {
                            setLoader(true);
                            const result2 = await logic.postSourceDocumentMarkCompleted(organizationId, document.id, false);
                            setLoader(false);
                            if (result2.hasErrors) {
                                setMarkCompletedResponseList([]);
                                toast(t("voucherRegistration.errorSave") + result2.errors.join(", "))
                            } else {
                                if (result2.value.length > 0) { //Any errors or warnings? If so show them to the user
                                    handleOpenSaveDialog();
                                    setMarkCompletedResponseList(result2.value);
                                } else {
                                    toast(t("voucherRegistration.documentSaved"));
                                    handleCloseSaveDialog();
                                    handleUpdateDocument("next");
                                }
                            }
                        }
                    }
                }
            }
        } else {
            toast(t("basic.errorWithOrganization"));
            props.closeDialog();
        }
    }
    const handleDocumentReSave = async () => {
        const logic = new SourceDocumentBusinessLogic();

        if (organizationId) {
            setLoader(true);
            const result = await logic.postSourceDocumentMarkCompleted(organizationId, document.id, ignoreWarnings);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("voucherRegistration.errorSave") + result.errors.join(", "))
            } else {
                if (result.value.length > 0) {
                    setMarkCompletedResponseList(result.value);
                } else {
                    toast(t("voucherRegistration.documentSaved"));
                    handleCloseSaveDialog();
                    handleUpdateDocument("next");
                }
            }
        } else {
            toast(t("basic.errorWithOrganization"));
            props.closeDialog();
        }
    }

    const handleFileViewedChange = (event: SelectChangeEvent) => {
        if (document && document.files) {
            let file = document.files.find(e => e.id === parseInt(event.target.value));
            setFileViewed(file ? file : null);
        }
    };

    //const [editorState, setEditorState] = useState(() => EditorState.createEmpty(),);

    const [quill, setQuill] = useState("");

    function handleDocumentAddLine(copyPreviousLine?: boolean) {
        if (document) {
            let lines: ISourceDocumentLineGuiModel[] = document.lines ? document.lines : [];
            if (copyPreviousLine && lines.length > 0) {
                setDocument(
                    {
                        ...document,
                        lines: [...lines,
                            {
                                ...lines[lines.length - 1],
                                debitAccount: undefined,
                                debitVat: undefined,
                                creditAccount: undefined,
                                creditVat: undefined,
                                crossReference: undefined,
                                crossReferenceError: false,
                            }
                        ]
                    }
                )
            } else {
                setDocument(
                    {
                        ...document,
                        lines: [...lines,
                        {
                            invoiceNo: undefined,
                            debitAccount: undefined,
                            debitVat: undefined,
                            creditAccount: undefined,
                            creditVat: undefined,
                            amount: undefined,
                            voucherDate: null,
                            dueDate: null,
                            valueDate: null,
                            project: undefined,
                            department: undefined,
                            text: undefined,
                            crossReference: undefined,
                            cid: undefined,
                            currency: undefined,
                            currencyVismaId: undefined,
                            currencyRate: undefined,
                            currencyAmount: undefined,
                            currencyDate: null
                        }
                        ],
                    });
            }
        }   
    }
    function handleDocumentAddLines(newLines: ISourceDocumentLineGuiModel[], index?: number) {
        if (document) {
            let lines: ISourceDocumentLineGuiModel[] = document.lines ? document.lines : [];

            if ((index || index === 0) && index !== (lines.length - 1)) {

                let firstLines = lines.slice(0, index + 1);
                let secondLines = lines.slice(index + 1, lines.length)

                setDocument(
                    {
                        ...document,
                        lines: [...firstLines, ...newLines, ...secondLines]
                    }
                )

            }
            else {
                setDocument(
                    {
                        ...document,
                        lines: [...lines, ...newLines]
                    }
                )
            }
        }
    }
    function handleDocumentUpdateLine(lineIndex: number, newLine: ISourceDocumentLineGuiModel) {
        if (document && document.lines) {
            setDocument({
                ...document,
                lines: document.lines.map((line, index) => {
                    if (index === lineIndex) {
                        return newLine;
                    } else {
                        return line;
                    }
                })
            })
        }
    }
    function handleDocumentDeleteLine(lineIndex: number) {
        if (document && document.lines) {
            setDocument({
                ...document,
                lines: document.lines.filter((line, index) => index !== lineIndex)
            });
        }
    }

    function handleDocumentComment(comment: string) {
        if (document) {
            setDocument({
                ...document,
                comments: comment
            })
        }
    }

    //Height calculations

    const windowHeight = "100vh";
    const dialogTitleHeight = "4rem";
    const dialogContentHeight = "calc(" + windowHeight + " - " + dialogTitleHeight + " - 0.2rem)";


    function validateData(): boolean {

        if (!document) {
            toast(t("voucherRegistration.errorDocument"));
            return false;

        } else if (
            document.lines &&
            document.lines.length > 0 &&
            (
                document.lines.findIndex(e => e.debitAccountError === true) !== -1 ||
                document.lines.findIndex(e => e.debitVatError === true) !== -1 ||
                document.lines.findIndex(e => e.creditAccountError === true) !== -1 ||
                document.lines.findIndex(e => e.creditVatError === true) !== -1 ||

                document.lines.findIndex(e => e.amountError === true) !== -1 ||

                document.lines.findIndex(e => e.voucherDateError === true) !== -1 ||
                document.lines.findIndex(e => e.dueDateError === true) !== -1 ||
                document.lines.findIndex(e => e.valueDateError === true) !== -1 ||
                document.lines.findIndex(e => e.departmentError === true) !== -1 ||
                document.lines.findIndex(e => e.projectError === true) !== -1 ||

                document.lines.findIndex(e => e.crossReferenceError === true) !== -1
            )
        ) {

            toast(t("voucherRegistration.errorLines"));
            return false;

        } else {
            return true;
        }
    }

    //#######################################################################
    //#######################################################################
    //#######################################################################
    return <Dialog
        disableEnforceFocus disableRestoreFocus
        fullScreen
        onClose={(event, reason) => props.closeDialog(reason)}
        open={props.open}
    >

        <DialogTitle height={dialogTitleHeight} >
            <Grid container justifyContent="space-between">
                <Grid>
                    <Typography variant="button" color={theme.palette.primary.main}>
                        {createNew ? t("voucherRegistration.newDocument") : t("voucherRegistration.documentId") + ": " + document.id}
                    </Typography>
                </Grid>
                <Grid>
                    <ButtonGroup variant="outlined" disabled={createNew} sx={{ display: createNew ? "none" : "block" }}>
                        <Button disabled={documentIndex === 0} onClick={() => handleUpdateDocument("previous")}>
                            <ArrowBack />
                        </Button>
                        <Button onClick={() => handleUpdateDocument("update")}>
                            {t("basic.document")}
                        </Button>
                        <Button disabled={documentIndex === (props.initialDocumentList.length - 1)} onClick={() => handleUpdateDocument("next")}>
                            <ArrowForward />
                        </Button>
                    </ButtonGroup>
                </Grid>
                <Grid>
                    <Stack direction="row" spacing={4}>
                        <ButtonGroup variant="contained" disabled={document.status === "Transferred" || document.status === "AdditionTransferred" || document.status === "Rejected" ? true : false}>
                            <Button disabled={createNew ? true : false} onClick={handleOpenRejectDialog} size="medium" color="error" startIcon={<ThumbDown />} >{t("approver.reject")}</Button>
                            <Button onClick={() => handleDocumentSave("draft")} size="medium" color="primary" startIcon={<SaveAlt />}>{t("basic.saveDraft")}</Button>
                            <Button onClick={() => handleDocumentSave("permanent")} size="medium" color="success" startIcon={<Save />}>{t("basic.save")}</Button>
                        </ButtonGroup>
                        <IconButton onClick={() => props.closeDialog("escapeKeyDown")} color="warning"><Clear/></IconButton>
                    </Stack>
                </Grid>
            </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ height: dialogContentHeight, padding:0 }}>
            <Grid container height="100%">
                <Grid xs={12} lg={7} flex="1 1 auto">
                    <DialogVoucherRegistrationLines
                        lines={document.lines ? document.lines : []}
                        comment={document.comments ? document.comments : ""}
                        status={document.status}
                        handleAddLine={handleDocumentAddLine}
                        handleDocumentAddLines={handleDocumentAddLines}
                        handleUpdateLine={handleDocumentUpdateLine}
                        handleDeleteLine={handleDocumentDeleteLine}
                        handleUpdateComment={handleDocumentComment}
                        banking={props.banking} />
                </Grid>
                <Grid xs={12} lg={5} flex="1 1 auto">
                    <Stack
                        direction="column"
                        height="100%"
                        padding={createNew ? 0 : theme.spacing(2)}
                        paddingLeft={createNew ? theme.spacing(2) : 0}
                        paddingRight={createNew ? theme.spacing(2) : 0}
                        spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                           
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'nowrap' }}>
                                
                                <FormControl color="warning" size="small" sx={{ minWidth: "11rem", maxWidth: "16rem", display: createNew ? "none" : "inline-flex" }}>
                                    <InputLabel id="select-doc">{t("search.documentChoice")}</InputLabel>
                                    <Select
                                        color="warning"
                                        labelId="select-doc-label"
                                        id="select-doc"
                                        value={fileViewed?.id.toString()}
                                        label={t("search.documentChoice")}
                                        onChange={handleFileViewedChange}
                                    >
                                        {document && document.files ? document.files.map(f =>
                                            <MenuItem key={f.id} value={f.id}>{f.fileName}</MenuItem>
                                        ) : <MenuItem value={undefined}>{t("basic.noFiles")}</MenuItem>}
                                    </Select>
                                </FormControl>

                                {fileViewed != null && fileViewed.fileName?.substring(fileViewed.fileName.length - 3) === "xml" && document !== null && documentIndex !== null ?
                                    <>
                                        <Button color="warning" sx={{ minWidth: "4rem" }}
                                            onClick={() => logicGetFile(organizationId ? organizationId : "", fileViewed.id, document.id + "")}
                                            variant="outlined" size="small">
                                            <Download fontSize="small" />
                                        </Button>
                                        <Button color="warning"
                                            onClick={() => logicGetFilePDF(organizationId ? organizationId : "", fileViewed.id, document.id + "")}
                                            startIcon={<Download />}
                                            variant="outlined" size="small" sx={{ width: "11rem", }}>
                                            {t("search.download")}
                                        </Button>
                                    </>
                                    :
                                    fileViewed != null && document !== null && documentIndex !== null ?
                                        <Button color="warning" sx={{ minWidth: "4rem" }}
                                            onClick={() => logicGetFile(organizationId ? organizationId : "", fileViewed.id, document.id + "")}
                                            variant="outlined" size="small">
                                            <Download fontSize="small" />
                                        </Button>
                                        : null
                                }
                            </Stack>

                            <Button
                                disabled={document.status === "Transferred" || document.status === "AdditionTransferred" || document.status === "Rejected"}
                                variant="outlined"
                                component="label"
                                startIcon={<NoteAdd />}
                                sx={{ display: createNew ? "none" : "inline-flex" }}>
                                {t("basic.addFile")}
                                <input
                                    hidden
                                    type="file"
                                    accept="image/png, image/gif, image/jpeg, application/pdf"
                                    onChange={(f: ChangeEvent<HTMLInputElement>) => handleUploadFileChange(f)} />
                            </Button>
                        </Stack>
                        
                        {createNew ?
                            <ReactQuill theme="snow" value={quill} onChange={setQuill} /> :
                            <FileViewer
                                showFile={true}
                                showFileToggle={undefined}
                                sourceDocumentId={document ? document.id : undefined}
                                file={fileViewed ? fileViewed : undefined} />}
                    </Stack>
                </Grid>

            </Grid>
        </DialogContent>
        <Dialog
            maxWidth="xs"
            fullWidth
            onClose={(event, reason) => handleCloseRejectDialog()}
            open={rejectDialog}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid><Typography variant="h6">{t("voucherRegistration.rejectDocument")}</Typography></Grid>
                    <Grid><Button variant="text" onClick={() => handleCloseRejectDialog()}>{t("basic.close")}</Button></Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Button variant="contained" color="error" onClick={handleDocumentReject} fullWidth>{t("approver.reject")}</Button>
            </DialogContent>
        </Dialog>
        <Dialog
            maxWidth="sm"
            fullWidth
            onClose={(event, reason) => handleCloseSaveDialog()}
            open={saveDialog}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid><Typography variant="h6">{t("voucherRegistration.saveDocument")}</Typography></Grid>
                    <Grid><Button variant="text" onClick={() => handleCloseSaveDialog()}>{t("basic.close")}</Button></Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <TableContainer>
                    <Table stickyHeader={true}>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>{t("basic.message")}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {markcompletedResponseList.map(e => {
                                return <TableRow>
                                    <TableCell>{e.rowNo}</TableCell>
                                    <TableCell>{e.validationType}</TableCell>
                                    <TableCell>{e.value}</TableCell>
                                    <TableCell>{e.isWarning ? <Warning color="warning" /> : <Error color="error" />}</TableCell>
                                </TableRow>
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <FormControlLabel control={<Switch checked={ignoreWarnings} onChange={e => setIgnoreWarnings(e.target.checked)} />} label={t("voucherRegistration.ignoreWarnings")} />
                <Button disabled={markcompletedResponseList.find(e => e.isWarning === false) ? true : ignoreWarnings ? false : true} variant="contained" color="success" onClick={handleDocumentReSave} fullWidth>{t("basic.save")}</Button>
            </DialogActions>
        </Dialog>
        <Dialog
            maxWidth="xs"
            fullWidth
            onClose={(event, reason) => handleCloseProcessDialog()}
            open={processDialog}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid><Typography variant="h6">{t("voucherRegistration.accrual")}</Typography></Grid>
                    <Grid><Button variant="text" onClick={() => handleCloseProcessDialog()}>{t("basic.close")}</Button></Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Button variant="contained" color="error" onClick={undefined} fullWidth>{t("basic.save")}</Button>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={undefined} fullWidth>{t("basic.save")}</Button>
            </DialogActions>
        </Dialog>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </Dialog>
}
export default DialogVoucherRegistration;